import React from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Button from "@amzn/awsui-components-core/button";
import Spinner from "@amzn/awsui-components-core/spinner";
import { UserProvider, useUser } from 'src/context/UserContext';
import { redirectToLogin, restartLogin, signInFailed, getAuthError } from 'src/services/session';
import Header from './Header';
import Dashboard from './Dashboard';

const CenteredSpinner = () => (
    <>
        <Header />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <Spinner size="large" />
        </div>
    </>
);

/** Human-readable message for the server's auth_error reason. */
const authErrorMessage = (reason: string | null): string => {
    switch (reason) {
        case 'exchange':
            return "We couldn't complete sign-in (the token exchange with Federate failed). This is usually a configuration issue rather than something you did.";
        case 'federate':
            return 'Federate reported an error during sign-in.';
        case 'nocode':
            return 'Sign-in was interrupted before it completed.';
        default:
            return "Sign-in didn't complete after several attempts.";
    }
};

/**
 * Shown when sign-in has definitively failed. Stops the auto-redirect loop and
 * tells the user, with a button to try again (which resets the attempt counter
 * and restarts the flow).
 */
const SignInFailed = () => (
    <>
        <Header />
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            <Alert type="error" header="Sign-in failed">
                {authErrorMessage(getAuthError())} If this keeps happening, contact support.
                <div style={{ marginTop: '16px' }}>
                    <Button variant="primary" onClick={() => restartLogin()}>
                        Try again
                    </Button>
                </div>
            </Alert>
        </div>
    </>
);

/**
 * SSO gate. This app has no sign-in page or button — when there is no
 * authenticated user we automatically redirect to Federate/Midway, preserving
 * the current route. A spinner shows for the brief moment before the browser
 * navigates away.
 *
 * If sign-in has failed (server reported an error, or we've looped too many
 * times without a session) we stop redirecting and show a failure notice instead
 * of spinning forever. If sign-in can't run in this environment (e.g. local dev
 * with no Federate), we show a minimal notice.
 */
const SignInRedirect = () => {
    const failed = signInFailed();
    const [canRedirect, setCanRedirect] = React.useState(!failed);

    React.useEffect(() => {
        if (failed) return;
        setCanRedirect(redirectToLogin());
    }, [failed]);

    if (failed) {
        return <SignInFailed />;
    }
    if (canRedirect) {
        return <CenteredSpinner />;
    }
    return (
        <>
            <Header />
            <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
                <Alert type="info" header="Not signed in">
                    Interactive Midway sign-in isn't available in this environment.
                </Alert>
            </div>
        </>
    );
};

const AppContent = () => {
    const { user, loading } = useUser();

    if (loading) {
        return <CenteredSpinner />;
    }

    if (!user) {
        return <SignInRedirect />;
    }

    return (
        <>
            <Header />
            <Dashboard />
        </>
    );
};

const App = () => {
    return (
        <UserProvider>
            <AppContent />
        </UserProvider>
    );
};

export default App;
